import type { CornerSnap } from './wall-model.js';
import type { Mm } from './units.js';

/**
 * SPECIFICATION.md clause 9.2 — component, direct and angular values between
 * two snapped corners.
 *
 * Both roundings are explicitly HALF-UP, not banker's rounding: a 0.5 mm
 * residual reads as the larger millimetre.
 */
export interface Measurement {
  readonly start: CornerSnap;
  readonly end: CornerSnap;
  readonly xDistance: Mm;
  readonly yDistance: Mm;
  readonly absoluteDistance: Mm;
  /** Direct distance rounded half-up to the nearest whole millimetre. */
  readonly roundedAbsoluteDistance: number;
  /** Unsigned inclination from horizontal, 0 to 90 degrees. */
  readonly angleFromHorizontal: number;
  /** Inclination rounded half-up to the nearest tenth of a degree. */
  readonly roundedAngleFromHorizontal: number;
}

export function measure(start: CornerSnap, end: CornerSnap): Measurement {
  const xDistance = Math.abs(end.x - start.x);
  const yDistance = Math.abs(end.y - start.y);
  const absoluteDistance = Math.hypot(end.x - start.x, end.y - start.y);
  const angleFromHorizontal = (Math.atan2(yDistance, xDistance) * 180) / Math.PI;
  return {
    start,
    end,
    xDistance,
    yDistance,
    absoluteDistance,
    roundedAbsoluteDistance: Math.floor(absoluteDistance + 0.5),
    angleFromHorizontal,
    roundedAngleFromHorizontal: Math.floor(angleFromHorizontal * 10 + 0.5) / 10,
  };
}
